import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Card,
  Group,
  Loader,
  Modal,
  Paper,
  Select,
  Stack,
  Switch,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  IconAlertTriangle,
  IconArrowLeft,
  IconBellRinging,
  IconBuildingStore,
  IconCamera,
  IconHammer,
  IconLogout,
  IconMessageCircle,
  IconPhone,
  IconSend,
  IconSettings,
  IconUserPlus,
  IconUsers,
  IconX,
} from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MutableRefObject,
} from "react";
import type {
  AvatarState,
  ClientMessage,
  FriendLink,
  RoomLayoutOption,
  RoomJoinMode,
  RoomThemeRuntime,
  RoomStatePayload,
  RoomThemeOption,
} from "../../shared/types";
import { ROOM_TYPE_CONFIG } from "../../shared/types";
import {
  createDefaultLayoutForRoomType,
  getDoorExitCell,
} from "../../shared/room-layout";
import {
  FRAME_RESIZE_FACTOR,
  scaleFrameDimensions,
} from "../../shared/frame-placement";
import { createDefaultRoomThemeRuntime } from "../../shared/room-theme";
import { canPlayEmoteForAvatarState } from "../../shared/avatar-animations";
import type { Session } from "../lib/api";
import {
  admitRoomKnock,
  blockUser,
  fetchLayouts,
  fetchRoomKnocks,
  fetchRoomThemes,
  fetchUnreadDMCount,
  rejectRoomKnock,
  savePicture,
  updateRoom,
  type FurnitureInventoryEntry,
  type FriendPresence,
  type RoomKnock,
} from "../lib/api";
import { AssetSelectionHUD } from "../components/AssetSelectionHUD";
import { AvatarPreviewCanvas } from "../components/AvatarPreviewCanvas";
import { BuildInventoryPanel } from "../components/BuildInventoryPanel";
import { FriendsPhonePanel } from "../components/FriendsPhonePanel";
import { ChatPanel } from "../components/ChatPanel";
import { LayoutPreviewCanvas } from "../components/LayoutPreviewCanvas";
import { PlacementConfirmHUD } from "../components/PlacementConfirmHUD";
import { PlayerProfileCard } from "../components/PlayerProfileCard";
import { KnocksPanel } from "../components/KnocksPanel";
import { PlayersPanel } from "../components/PlayersPanel";
import { RoomCanvas, type PlacementPhase } from "../components/RoomCanvas";
import type { ChatLogEntry, RoomMotionFeed } from "../hooks/useRoomConnection";
import type { SelectedAsset } from "../hooks/useMarketplace";
import { useInventory } from "../hooks/useInventory";
import { DROBBIT_WAVE_DURATION_MS } from "../lib/drobbit-model";
import { glassPanel, glassPanelDense } from "../lib/glass";

interface Props {
  session: Session;
  roomId: string;
  roomName?: string;
  roomOwnerAddress?: string;
  roomIsPrivate?: boolean;
  roomJoinMode?: RoomJoinMode;
  roomTokenGateContract?: string;
  roomAllowKnock?: boolean;
  roomState: RoomStatePayload | null;
  stateSampleTimeMs: number | null;
  selfId: string | null;
  waitlistPosition: number | null;
  chatLog: ChatLogEntry[];
  connected: boolean;
  sendSocket: (msg: ClientMessage) => void;
  motionFeedRef: MutableRefObject<RoomMotionFeed>;
  onBack: () => void;
  onOpenMarketplace: () => void;
  onRefreshFurnitureInventory?: () => Promise<void>;
  furnitureInventory: FurnitureInventoryEntry[];
  onSelectFurniture: (entry: FurnitureInventoryEntry) => void;
  friendLinks: FriendLink[];
  pendingFriends: FriendLink[];
  onAcceptFriend: (friend: FriendLink) => Promise<void>;
  onRemoveFriend?: (friend: FriendLink) => Promise<void>;
  friendsOnline: FriendPresence[];
  dmNotificationSeq?: number;
  lastDmNotification?: {
    fromAddress: string;
    fromName: string;
    text?: string;
    seq: number;
  } | null;
  unreadPerFriend?: Record<string, number>;
  onRefreshUnread?: () => void;
  idleWarningSeconds?: number | null;
  onDismissIdleWarning?: () => void;
  onVisitFriendRoom?: (roomId: string) => void;
  onAddFriend?: (address: string) => Promise<void>;
  onReportUser?: (address: string, roomId: string) => Promise<void>;
  onRoomSettingsUpdated?: () => void;
  externalSelectedAsset?: SelectedAsset | null;
  onClearExternalAsset?: () => void;
}

export function RoomScreen({
  session,
  roomId,
  roomName,
  roomOwnerAddress,
  roomIsPrivate,
  roomJoinMode,
  roomTokenGateContract,
  roomAllowKnock,
  roomState,
  stateSampleTimeMs,
  selfId,
  waitlistPosition,
  chatLog,
  connected,
  sendSocket,
  motionFeedRef,
  onBack,
  onOpenMarketplace,
  onRefreshFurnitureInventory,
  furnitureInventory,
  onSelectFurniture,
  friendLinks,
  pendingFriends,
  onAcceptFriend,
  onRemoveFriend,
  friendsOnline,
  dmNotificationSeq,
  lastDmNotification,
  unreadPerFriend,
  onRefreshUnread,
  idleWarningSeconds,
  onDismissIdleWarning,
  onVisitFriendRoom,
  onAddFriend,
  onReportUser,
  onRoomSettingsUpdated,
  externalSelectedAsset,
  onClearExternalAsset,
}: Props) {
  const [editMode, setEditMode] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const bottomBarInputRef = useRef<HTMLInputElement | null>(null);

  // Panels
  const [chatOpen, { toggle: toggleChat, close: closeChat }] =
    useDisclosure(false);
  const [playersOpen, { toggle: togglePlayers, close: closePlayers }] =
    useDisclosure(false);
  const [knocksOpen, { toggle: toggleKnocks, close: closeKnocks }] =
    useDisclosure(false);
  const [phoneOpen, { toggle: togglePhone, close: closePhone }] =
    useDisclosure(false);
  const [unreadDMCount, setUnreadDMCount] = useState(0);
  const [bottomBarChatText, setBottomBarChatText] = useState("");

  // Placement state machine
  const [placementPhase, setPlacementPhase] = useState<PlacementPhase>("idle");
  const [placingAsset, setPlacingAsset] = useState<SelectedAsset | null>(null);
  const [placementRotation, setPlacementRotation] = useState(0);
  const [confirmedPlacement, setConfirmedPlacement] = useState<{
    x: number;
    y: number;
    z: number;
    rotation?: number;
  } | null>(null);

  // Asset selection
  const [selectedAssetId, setSelectedAssetId] = useState<string | null>(null);
  const [movingAssetId, setMovingAssetId] = useState<string | null>(null);

  // Avatar profile panel
  const [selectedProfile, setSelectedProfile] = useState<AvatarState | null>(
    null
  );
  const [sendingFriendRequest, setSendingFriendRequest] = useState(false);
  const [acceptingFriendRequest, setAcceptingFriendRequest] = useState(false);
  const [sendingReport, setSendingReport] = useState(false);
  const [selfEmoteWheelOpen, setSelfEmoteWheelOpen] = useState(false);
  const [pendingExit, setPendingExit] = useState(false);
  const [layoutChoices, setLayoutChoices] = useState<RoomLayoutOption[]>([]);
  const [themeChoices, setThemeChoices] = useState<RoomThemeOption[]>([]);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [sceneReloadSeq, setSceneReloadSeq] = useState(0);
  const [pendingKnocks, setPendingKnocks] = useState<RoomKnock[]>([]);
  const [knockActionId, setKnockActionId] = useState<string | null>(null);

  const roomType = roomState?.roomType ?? "home";
  const fallbackLayout = useMemo(
    () => ({
      id: `fallback-${roomType}`,
      name: "Classic Layout",
      access: "free" as const,
      maxUsers: ROOM_TYPE_CONFIG[roomType].capacity,
      ...createDefaultLayoutForRoomType(roomType),
    }),
    [roomType]
  );
  const activeLayout = roomState?.layout ?? fallbackLayout;
  const fallbackTheme = useMemo(() => createDefaultRoomThemeRuntime(), []);
  const activeTheme = roomState?.theme ?? fallbackTheme;
  const [exitTileX, exitTileZ] = useMemo(
    () => getDoorExitCell(activeLayout),
    [activeLayout]
  );
  const [currentRoomName, setCurrentRoomName] = useState(
    roomName?.trim() || roomId
  );
  const [currentRoomIsPrivate, setCurrentRoomIsPrivate] = useState(
    Boolean(roomIsPrivate)
  );
  const [currentRoomJoinMode, setCurrentRoomJoinMode] = useState<RoomJoinMode>(
    roomJoinMode ?? (roomIsPrivate ? "members" : "open")
  );
  const [currentRoomTokenGateContract, setCurrentRoomTokenGateContract] =
    useState(roomTokenGateContract ?? "");
  const [currentRoomAllowKnock, setCurrentRoomAllowKnock] = useState(
    roomAllowKnock ?? true
  );
  const [settingsName, setSettingsName] = useState(roomName?.trim() || roomId);
  const [settingsIsPrivate, setSettingsIsPrivate] = useState(
    Boolean(roomIsPrivate)
  );
  const [settingsJoinMode, setSettingsJoinMode] = useState<RoomJoinMode>(
    roomJoinMode ?? (roomIsPrivate ? "members" : "open")
  );
  const [settingsJoinPassword, setSettingsJoinPassword] = useState("");
  const [settingsTokenGateContract, setSettingsTokenGateContract] = useState(
    (roomTokenGateContract ?? "").split(":")[0] ?? ""
  );
  const [settingsTokenGateTokenId, setSettingsTokenGateTokenId] = useState(
    (roomTokenGateContract ?? "").split(":")[1] ?? ""
  );
  const [settingsAllowKnock, setSettingsAllowKnock] = useState(
    roomAllowKnock ?? true
  );
  const [settingsLayoutId, setSettingsLayoutId] = useState<string | null>(
    activeLayout.id
  );
  const activeWallThemeId = activeTheme.wallThemeId ?? activeTheme.id;
  const activeFloorThemeId = activeTheme.floorThemeId ?? activeTheme.id;
  const [settingsWallThemeId, setSettingsWallThemeId] = useState<string | null>(
    activeWallThemeId
  );
  const [settingsFloorThemeId, setSettingsFloorThemeId] = useState<
    string | null
  >(activeFloorThemeId);
  const settingsPreviewTheme = useMemo<RoomThemeRuntime>(() => {
    const byId = new Map(
      themeChoices.map((theme) => [theme.id, theme] as const)
    );
    const wallTheme =
      byId.get(settingsWallThemeId ?? "") ?? byId.get(activeWallThemeId);
    const floorTheme =
      byId.get(settingsFloorThemeId ?? "") ?? byId.get(activeFloorThemeId);
    return {
      id: wallTheme?.id ?? activeWallThemeId,
      name: wallTheme?.name ?? activeTheme.wallThemeName ?? activeTheme.name,
      access: wallTheme?.access ?? activeTheme.access,
      wall: wallTheme?.wall ?? activeTheme.wall,
      floorTop: floorTheme?.floorTop ?? activeTheme.floorTop,
      floorSide: floorTheme?.floorSide ?? activeTheme.floorSide,
      porchMarker: floorTheme?.porchMarker ?? activeTheme.porchMarker,
      wallThemeId: wallTheme?.id ?? activeWallThemeId,
      wallThemeName:
        wallTheme?.name ?? activeTheme.wallThemeName ?? activeTheme.name,
      floorThemeId: floorTheme?.id ?? activeFloorThemeId,
      floorThemeName:
        floorTheme?.name ?? activeTheme.floorThemeName ?? activeTheme.name,
    };
  }, [
    themeChoices,
    settingsWallThemeId,
    settingsFloorThemeId,
    activeWallThemeId,
    activeFloorThemeId,
    activeTheme,
  ]);
  const displayRoomName = currentRoomName.trim() || roomId;
  const hasAuthoritativeRoomState = roomState?.roomId === roomId;
  const showRoomLoadingScreen = !hasAuthoritativeRoomState;
  const roomLoadingMessage = connected
    ? waitlistPosition != null
      ? `Joining queue (#${waitlistPosition})...`
      : "Synchronizing room state..."
    : "Connecting to room...";
  const normalizedSessionAddress = session.address.trim();
  const normalizedSessionAddressLower = normalizedSessionAddress.toLowerCase();
  const isRoomOwner =
    roomOwnerAddress?.trim().toLowerCase() === normalizedSessionAddressLower;
  const canBuildRoom =
    isRoomOwner || (session.isAdmin && !currentRoomIsPrivate);
  const ownerAddressLabel = roomOwnerAddress
    ? `${roomOwnerAddress.slice(0, 8)}...${roomOwnerAddress.slice(-6)}`
    : "unknown";
  const selfAvatar =
    roomState?.players.find((avatar) => avatar.id === selfId) ?? null;

  useEffect(() => {
    setCurrentRoomName(roomName?.trim() || roomId);
  }, [roomId, roomName]);

  useEffect(() => {
    setCurrentRoomIsPrivate(Boolean(roomIsPrivate));
  }, [roomIsPrivate]);

  useEffect(() => {
    setCurrentRoomJoinMode(
      roomJoinMode ?? (roomIsPrivate ? "members" : "open")
    );
  }, [roomIsPrivate, roomJoinMode]);

  useEffect(() => {
    setCurrentRoomTokenGateContract(roomTokenGateContract ?? "");
  }, [roomTokenGateContract]);

  useEffect(() => {
    setCurrentRoomAllowKnock(roomAllowKnock ?? true);
  }, [roomAllowKnock]);

  useEffect(() => {
    setSettingsLayoutId(activeLayout.id);
  }, [activeLayout.id]);

  useEffect(() => {
    setSettingsWallThemeId(activeWallThemeId);
  }, [activeWallThemeId]);

  useEffect(() => {
    setSettingsFloorThemeId(activeFloorThemeId);
  }, [activeFloorThemeId]);

  useEffect(() => {
    if (!isRoomOwner) {
      setLayoutChoices([]);
      setThemeChoices([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const [layouts, themes] = await Promise.all([
          fetchLayouts(session),
          fetchRoomThemes(session),
        ]);
        if (cancelled) return;
        const layoutOptions = layouts.filter(
          (layout) => layout.state === "published" && layout.unlocked
        );
        if (!layoutOptions.some((option) => option.id === activeLayout.id)) {
          layoutOptions.unshift({
            id: activeLayout.id,
            name: `${activeLayout.name} (current)`,
            slug: activeLayout.id,
            state: "published",
            access: activeLayout.access,
            maxUsers: activeLayout.maxUsers,
            width: activeLayout.width,
            height: activeLayout.height,
            tiles: activeLayout.tiles,
            walls: activeLayout.walls,
            door: activeLayout.door,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            requiresUnlock: false,
            unlocked: true,
          });
        }
        const themeOptions = themes.filter(
          (theme) => theme.state === "published" && theme.unlocked
        );
        if (!themeOptions.some((option) => option.id === activeWallThemeId)) {
          themeOptions.unshift({
            id: activeWallThemeId,
            name: `${
              activeTheme.wallThemeName ?? activeTheme.name
            } (current wallpaper)`,
            slug: activeWallThemeId,
            state: "published",
            access: activeTheme.access,
            wall: activeTheme.wall,
            floorTop: activeTheme.floorTop,
            floorSide: activeTheme.floorSide,
            porchMarker: activeTheme.porchMarker,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            requiresUnlock: false,
            unlocked: true,
          });
        }
        if (!themeOptions.some((option) => option.id === activeFloorThemeId)) {
          themeOptions.unshift({
            id: activeFloorThemeId,
            name: `${
              activeTheme.floorThemeName ?? activeTheme.name
            } (current floor)`,
            slug: activeFloorThemeId,
            state: "published",
            access: activeTheme.access,
            wall: activeTheme.wall,
            floorTop: activeTheme.floorTop,
            floorSide: activeTheme.floorSide,
            porchMarker: activeTheme.porchMarker,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            requiresUnlock: false,
            unlocked: true,
          });
        }
        setLayoutChoices(layoutOptions);
        setThemeChoices(themeOptions);
      } catch {
        if (!cancelled) {
          setLayoutChoices([]);
          setThemeChoices([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    isRoomOwner,
    roomId,
    activeLayout.id,
    activeLayout.name,
    activeWallThemeId,
    activeFloorThemeId,
    activeTheme.name,
    activeTheme.wallThemeName,
    activeTheme.floorThemeName,
    session.address,
    session.token,
  ]);

  useEffect(() => {
    if (!isRoomOwner) {
      setPendingKnocks([]);
      return;
    }
    let cancelled = false;
    const refresh = async () => {
      try {
        const knocks = await fetchRoomKnocks(session, roomId);
        if (!cancelled) {
          setPendingKnocks(knocks);
        }
      } catch {
        if (!cancelled) {
          setPendingKnocks([]);
        }
      }
    };
    void refresh();
    const timer = window.setInterval(() => {
      void refresh();
    }, 6000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [isRoomOwner, roomId, session]);

  // Auto-close notifications panel when nothing remains
  const knockCount = isRoomOwner ? pendingKnocks.length : 0;
  useEffect(() => {
    if (knocksOpen && knockCount === 0 && pendingFriends.length === 0) {
      closeKnocks();
    }
  }, [knocksOpen, knockCount, pendingFriends.length, closeKnocks]);

  // Unread DM count polling
  const refreshUnreadCount = useCallback(async () => {
    try {
      const count = await fetchUnreadDMCount(session);
      setUnreadDMCount(count);
    } catch {
      // ignore
    }
  }, [session]);

  useEffect(() => {
    void refreshUnreadCount();
    const timer = window.setInterval(() => void refreshUnreadCount(), 15_000);
    return () => window.clearInterval(timer);
  }, [refreshUnreadCount]);

  // Instant refresh when a DM notification arrives via WebSocket
  useEffect(() => {
    if (dmNotificationSeq) {
      void refreshUnreadCount();
    }
  }, [dmNotificationSeq, refreshUnreadCount]);

  // Accepted friends for phone panel
  const acceptedFriendLinks = useMemo(
    () => friendLinks.filter((f) => f.status === "accepted"),
    [friendLinks]
  );
  const buildFurnitureInventory = useMemo(() => {
    return furnitureInventory
      .map((entry) => {
        const availableQuantity = Math.max(
          0,
          Math.round(entry.availableQuantity ?? 0)
        );
        if (availableQuantity <= 0) {
          return null;
        }
        if (availableQuantity === entry.quantity) {
          return entry;
        }
        return { ...entry, quantity: availableQuantity };
      })
      .filter((entry): entry is FurnitureInventoryEntry => entry != null);
  }, [furnitureInventory]);
  const ownPlacedFurnitureSignature = useMemo(() => {
    const signatures: string[] = [];
    for (const asset of roomState?.assets ?? []) {
      if (asset.type !== "glb") {
        continue;
      }
      if (
        (asset.ownerAddress?.trim().toLowerCase() ?? "") !==
        normalizedSessionAddressLower
      ) {
        continue;
      }
      const furnitureId = asset.furnitureId?.trim();
      if (!furnitureId) {
        continue;
      }
      const variantId = asset.colorVariantId?.trim() ?? "";
      signatures.push(`${asset.id}:${furnitureId}:${variantId}`);
    }
    signatures.sort();
    return signatures.join("|");
  }, [roomState?.assets, normalizedSessionAddressLower]);
  const didInitFurnitureRefresh = useRef(false);
  useEffect(() => {
    if (!onRefreshFurnitureInventory) {
      return;
    }
    if (!didInitFurnitureRefresh.current) {
      didInitFurnitureRefresh.current = true;
      return;
    }
    void onRefreshFurnitureInventory();
  }, [onRefreshFurnitureInventory, ownPlacedFurnitureSignature]);

  // Inventory
  const inventory = useInventory(session);

  useEffect(() => {
    if (!editMode || !onRefreshFurnitureInventory) {
      return;
    }
    void onRefreshFurnitureInventory();
  }, [editMode, onRefreshFurnitureInventory]);

  const normalizePlacementAsset = useCallback(
    (asset: SelectedAsset): SelectedAsset => {
      if (asset.type !== "glb") {
        return { ...asset };
      }
      return {
        ...asset,
        teleportGroup:
          asset.interactionType === "teleport"
            ? (asset.teleportGroup ?? "").trim()
            : undefined,
      };
    },
    []
  );

  useEffect(() => {
    if (editMode) {
      void inventory.load();
    }
  }, [editMode]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!canBuildRoom && editMode) {
      setEditMode(false);
    }
  }, [canBuildRoom, editMode]);

  // If an external asset comes in from the marketplace drawer, start placing it
  useEffect(() => {
    if (!externalSelectedAsset) return;
    if (!canBuildRoom) return;
    if (!editMode) {
      setEditMode(true);
    }
    setPlacingAsset(normalizePlacementAsset(externalSelectedAsset));
    setPlacementPhase("placing");
    setPlacementRotation(0);
    setConfirmedPlacement(null);
    setMovingAssetId(null);
    onClearExternalAsset?.();
  }, [externalSelectedAsset, onClearExternalAsset, canBuildRoom, normalizePlacementAsset]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset placement state and selection when exiting build mode
  useEffect(() => {
    if (!editMode) {
      setPlacementPhase("idle");
      setPlacingAsset(null);
      setPlacementRotation(0);
      setConfirmedPlacement(null);
      setSelectedAssetId(null);
      setMovingAssetId(null);
    }
  }, [editMode]);

  useEffect(() => {
    const selectedId = selectedProfile?.id;
    if (!selectedId || !roomState) return;
    const next = [...roomState.players, ...roomState.bots].find(
      (avatar) => avatar.id === selectedId
    );
    setSelectedProfile(next ?? null);
  }, [roomState, selectedProfile?.id]);

  useEffect(() => {
    if (!selfAvatar) {
      setSelfEmoteWheelOpen(false);
    }
  }, [selfAvatar]);

  useEffect(() => {
    if (!pendingExit || !selfId) return;
    let rafId = 0;
    const checkReachedExit = () => {
      const motion = motionFeedRef.current.byId.get(selfId);
      const x = motion?.x ?? selfAvatar?.x;
      const z = motion?.z ?? selfAvatar?.z;
      if (
        x != null &&
        z != null &&
        Math.abs(x - exitTileX) < 0.12 &&
        Math.abs(z - exitTileZ) < 0.12
      ) {
        setPendingExit(false);
        onBack();
        return;
      }
      rafId = window.requestAnimationFrame(checkReachedExit);
    };
    checkReachedExit();
    return () => {
      if (rafId !== 0) {
        window.cancelAnimationFrame(rafId);
      }
    };
  }, [
    pendingExit,
    selfId,
    selfAvatar,
    exitTileX,
    exitTileZ,
    onBack,
    motionFeedRef,
  ]);

  const rotatePlacementAsset = useCallback(() => {
    if (placingAsset?.type === "frame") {
      return;
    }
    setPlacementRotation((prev) => (prev + 90) % 360);
    if (placementPhase === "confirming") {
      // Rotation changes footprint/stack checks; force a fresh lock step.
      setPlacementPhase("placing");
      setConfirmedPlacement(null);
    }
  }, [placementPhase, placingAsset?.type]);

  const resizeFramePlacement = useCallback((scaleFactor: number) => {
    setPlacingAsset((current) => {
      if (!current || current.type !== "frame") {
        return current;
      }
      const resized = scaleFrameDimensions(
        current.frameWidth,
        current.frameHeight,
        scaleFactor
      );
      return {
        ...current,
        frameWidth: resized.width,
        frameHeight: resized.height,
      };
    });
  }, []);

  // Global keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Skip if typing in an input
      const tag = (e.target as HTMLElement).tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;

      switch (e.key.toLowerCase()) {
        case "t":
          toggleChat();
          closePlayers();
          break;
        case "p":
          togglePlayers();
          closeChat();
          break;
        case "b":
          if (canBuildRoom) {
            setEditMode((v) => !v);
          }
          break;
        case "s":
          void handleSnapshot();
          break;
        case "d":
          if (editMode) {
            if (
              movingAssetId &&
              (placementPhase === "placing" || placementPhase === "confirming")
            ) {
              e.preventDefault();
              handleDeleteMovingAsset();
            } else if (selectedAssetId && placementPhase === "idle") {
              e.preventDefault();
              handleDeleteSelected();
            }
            break;
          }
          if (!canPlayEmoteForAvatarState("dance", selfAvatar?.isSitting)) {
            break;
          }
          sendSocket({ type: "toggle_dance" });
          setSelfEmoteWheelOpen(false);
          break;
        case "e":
          handleToggleSelfEmoteWheel();
          break;
        case "r":
          if (
            editMode &&
            (placementPhase === "placing" || placementPhase === "confirming") &&
            placingAsset?.type !== "frame"
          ) {
            rotatePlacementAsset();
          }
          break;
        case "z":
          if (
            editMode &&
            (placementPhase === "placing" || placementPhase === "confirming") &&
            placingAsset?.type === "frame"
          ) {
            e.preventDefault();
            resizeFramePlacement(1 / FRAME_RESIZE_FACTOR);
          }
          break;
        case "x":
          if (
            editMode &&
            (placementPhase === "placing" || placementPhase === "confirming") &&
            placingAsset?.type === "frame"
          ) {
            e.preventDefault();
            resizeFramePlacement(FRAME_RESIZE_FACTOR);
          }
          break;
        case "escape":
          if (placementPhase === "confirming") {
            setPlacementPhase("placing");
            setConfirmedPlacement(null);
          } else if (placementPhase === "placing") {
            setPlacementPhase("idle");
            setPlacingAsset(null);
            setPlacementRotation(0);
            setMovingAssetId(null);
          } else if (selectedAssetId) {
            setSelectedAssetId(null);
          } else if (selfEmoteWheelOpen) {
            setSelfEmoteWheelOpen(false);
          } else if (selectedProfile) {
            setSelectedProfile(null);
          } else if (chatOpen) {
            closeChat();
          } else if (playersOpen) {
            closePlayers();
          }
          break;
        case "delete":
        case "backspace":
          if (!editMode) {
            break;
          }
          if (
            movingAssetId &&
            (placementPhase === "placing" || placementPhase === "confirming")
          ) {
            e.preventDefault();
            handleDeleteMovingAsset();
          } else if (selectedAssetId && placementPhase === "idle") {
            e.preventDefault();
            handleDeleteSelected();
          }
          break;
        case "enter":
          if (placementPhase === "confirming") {
            handleConfirmPlacement();
          } else {
            e.preventDefault();
            bottomBarInputRef.current?.focus();
          }
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }); // eslint-disable-line react-hooks/exhaustive-deps

  const handleMove = (x: number, z: number, facing?: number) => {
    setSelectedProfile(null);
    setSelfEmoteWheelOpen(false);
    setPendingExit(x === exitTileX && z === exitTileZ);
    sendSocket({ type: "move", x, z, facing });
  };

  const handleInteractAsset = useCallback(
    (assetId: string) => {
      const normalized = assetId.trim();
      if (!normalized) {
        return;
      }
      setSelectedProfile(null);
      setSelfEmoteWheelOpen(false);
      sendSocket({ type: "interact_asset", assetId: normalized });
    },
    [sendSocket]
  );

  const handleToggleSelfEmoteWheel = useCallback(() => {
    if (!selfAvatar) {
      return;
    }
    setSelectedProfile(null);
    setSelectedAssetId(null);
    setSelfEmoteWheelOpen((prev) => !prev);
  }, [selfAvatar]);

  const handleTriggerDanceEmote = useCallback(() => {
    if (!canPlayEmoteForAvatarState("dance", selfAvatar?.isSitting)) {
      setSelfEmoteWheelOpen(false);
      return;
    }
    sendSocket({ type: "toggle_dance" });
    setSelfEmoteWheelOpen(false);
  }, [sendSocket, selfAvatar?.isSitting]);

  const handleTriggerWaveEmote = useCallback(() => {
    sendSocket({ type: "Wave" });
    setSelfEmoteWheelOpen(false);
  }, [sendSocket]);

  const handleLockPosition = useCallback(
    (placement: { x: number; y: number; z: number; rotation?: number }) => {
      if (!canBuildRoom || !editMode) return;
      if (placementPhase === "placing" && placingAsset) {
        setConfirmedPlacement(placement);
        setPlacementPhase("confirming");
      }
    },
    [canBuildRoom, editMode, placementPhase, placingAsset]
  );

  const handleConfirmPlacement = useCallback(() => {
    if (!canBuildRoom || !editMode || !placingAsset || !confirmedPlacement) {
      return;
    }
    const placingTeleportGroup =
      placingAsset.type === "glb"
        ? placingAsset.teleportGroup?.trim() || undefined
        : undefined;
    if (
      placingAsset.type === "glb" &&
      placingAsset.interactionType === "teleport" &&
      !placingTeleportGroup
    ) {
      notifications.show({
        title: "Teleport link required",
        message: "Set a teleport link ID before placing this teleport item.",
        color: "red",
      });
      return;
    }
    const isMovingExistingAsset = Boolean(movingAssetId);
    if (
      !isMovingExistingAsset &&
      placingAsset.faContract &&
      placingAsset.tokenId &&
      (placingAsset.ownedEditions ?? 0) <= 0
    ) {
      notifications.show({
        title: "Cannot place",
        message: "You don't own any editions of this token.",
        color: "red",
      });
      return;
    }

    if (
      placingAsset.faContract &&
      placingAsset.tokenId &&
      typeof placingAsset.ownedEditions === "number" &&
      roomState
    ) {
      const matchingAssets = roomState.assets.filter(
        (asset) =>
          asset.type === placingAsset.type &&
          asset.ownerAddress === session.address &&
          asset.faContract === placingAsset.faContract &&
          asset.tokenId === placingAsset.tokenId
      );
      const movingAsset = movingAssetId
        ? roomState.assets.find((asset) => asset.id === movingAssetId)
        : null;
      const movingMatches =
        movingAsset?.type === placingAsset.type &&
        movingAsset.ownerAddress === session.address &&
        movingAsset.faContract === placingAsset.faContract &&
        movingAsset.tokenId === placingAsset.tokenId;
      const effectivePlacedCount =
        matchingAssets.length - (movingMatches ? 1 : 0);

      if (effectivePlacedCount >= placingAsset.ownedEditions) {
        notifications.show({
          title: "Placement limit reached",
          message: `You own ${placingAsset.ownedEditions} edition(s) of this token.`,
          color: "red",
        });
        return;
      }
    }

    sendSocket({
      type: "place_asset",
      asset: {
        id: movingAssetId ?? undefined,
        type: placingAsset.type,
        url: placingAsset.url,
        name: placingAsset.name,
        thumbnail: placingAsset.thumbnail,
        mediaMime: placingAsset.mediaMime,
        faContract: placingAsset.faContract,
        tokenId: placingAsset.tokenId,
        x: confirmedPlacement.x,
        y: confirmedPlacement.y,
        z: confirmedPlacement.z,
        rotation:
          placingAsset.type === "frame"
            ? confirmedPlacement.rotation ?? 0
            : placementRotation,
        frameWidth:
          placingAsset.type === "frame" ? placingAsset.frameWidth : undefined,
        frameHeight:
          placingAsset.type === "frame" ? placingAsset.frameHeight : undefined,
        furnitureId:
          placingAsset.type === "glb" ? placingAsset.furnitureId : undefined,
        tintColor:
          placingAsset.type === "glb" ? placingAsset.tintColor : undefined,
        blockWidth:
          placingAsset.type === "glb" ? placingAsset.blockWidth : undefined,
        blockDepth:
          placingAsset.type === "glb" ? placingAsset.blockDepth : undefined,
        sittable:
          placingAsset.type === "glb" ? placingAsset.sittable : undefined,
        interactionType:
          placingAsset.type === "glb"
            ? placingAsset.interactionType
            : undefined,
        walkableTop:
          placingAsset.type === "glb" ? placingAsset.walkableTop : undefined,
        stackableTop:
          placingAsset.type === "glb" ? placingAsset.stackableTop : undefined,
        stackHeight:
          placingAsset.type === "glb" ? placingAsset.stackHeight : undefined,
        teleportGroup:
          placingAsset.type === "glb" ? placingTeleportGroup : undefined,
        actionOpenAnimation:
          placingAsset.type === "glb"
            ? placingAsset.actionOpenAnimation
            : undefined,
        actionCloseAnimation:
          placingAsset.type === "glb"
            ? placingAsset.actionCloseAnimation
            : undefined,
        seatOffsetX:
          placingAsset.type === "glb" ? placingAsset.seatOffsetX : undefined,
        seatOffsetY:
          placingAsset.type === "glb" ? placingAsset.seatOffsetY : undefined,
        seatOffsetZ:
          placingAsset.type === "glb" ? placingAsset.seatOffsetZ : undefined,
        colorVariantId:
          placingAsset.type === "glb" ? placingAsset.colorVariantId : undefined,
        paletteColors:
          placingAsset.type === "glb" ? placingAsset.paletteColors : undefined,
      },
    });
    setPlacementPhase("idle");
    setPlacingAsset(null);
    setPlacementRotation(0);
    setConfirmedPlacement(null);
    setMovingAssetId(null);
  }, [
    editMode,
    placingAsset,
    confirmedPlacement,
    canBuildRoom,
    movingAssetId,
    placementRotation,
    roomState,
    sendSocket,
    session.address,
  ]);

  const handleSelectFromInventory = useCallback(
    (asset: SelectedAsset) => {
      if (!canBuildRoom || !editMode) return;
      setPlacingAsset(normalizePlacementAsset(asset));
      setPlacementPhase("placing");
      setPlacementRotation(0);
      setConfirmedPlacement(null);
      setSelectedAssetId(null);
      setMovingAssetId(null);
    },
    [canBuildRoom, editMode, normalizePlacementAsset]
  );

  const handleDeleteSelected = useCallback(() => {
    if (!canBuildRoom || !editMode || !selectedAssetId) return;
    sendSocket({ type: "remove_asset", assetId: selectedAssetId });
    setSelectedAssetId(null);
  }, [canBuildRoom, editMode, selectedAssetId, sendSocket]);

  const handleDeleteMovingAsset = useCallback(() => {
    if (!canBuildRoom || !editMode || !movingAssetId) return;
    sendSocket({ type: "remove_asset", assetId: movingAssetId });
    setMovingAssetId(null);
    setSelectedAssetId(null);
    setPlacementPhase("idle");
    setPlacingAsset(null);
    setPlacementRotation(0);
    setConfirmedPlacement(null);
  }, [canBuildRoom, editMode, movingAssetId, sendSocket]);

  const beginMoveAssetById = useCallback(
    (assetId: string) => {
      if (!canBuildRoom || !editMode || !roomState) return;
      const asset = roomState.assets.find((a) => a.id === assetId);
      if (!asset) return;
      setMovingAssetId(asset.id);
      setSelectedAssetId(null);
      setPlacingAsset({
        type: asset.type,
        url: asset.url,
        name: asset.name ?? "Object",
        thumbnail: asset.thumbnail,
        mediaMime: asset.mediaMime,
        faContract: asset.faContract,
        tokenId: asset.tokenId,
        frameWidth: asset.frameWidth,
        frameHeight: asset.frameHeight,
        furnitureId: asset.furnitureId,
        tintColor: asset.tintColor,
        blockWidth: asset.blockWidth,
        blockDepth: asset.blockDepth,
        sittable: asset.sittable,
        interactionType: asset.interactionType,
        walkableTop: asset.walkableTop,
        stackableTop: asset.stackableTop,
        stackHeight: asset.stackHeight,
        teleportGroup: asset.teleportGroup,
        actionOpenAnimation: asset.actionOpenAnimation,
        actionCloseAnimation: asset.actionCloseAnimation,
        seatOffsetX: asset.seatOffsetX,
        seatOffsetY: asset.seatOffsetY,
        seatOffsetZ: asset.seatOffsetZ,
        colorVariantId: asset.colorVariantId,
        paletteColors: asset.paletteColors,
      });
      setPlacementPhase("placing");
      setPlacementRotation(asset.type === "frame" ? 0 : asset.rotation ?? 0);
      setConfirmedPlacement(null);
    },
    [canBuildRoom, editMode, roomState]
  );

  const handleMoveSelected = useCallback(() => {
    if (!selectedAssetId) return;
    beginMoveAssetById(selectedAssetId);
  }, [selectedAssetId, beginMoveAssetById]);

  const handleSelectAsset = useCallback(
    (id: string | null) => {
      // Don't select while placing
      if (placementPhase !== "idle") return;
      setSelectedProfile(null);
      setSelfEmoteWheelOpen(false);
      if (!id) {
        setSelectedAssetId(null);
        return;
      }
      if (editMode && canBuildRoom) {
        beginMoveAssetById(id);
        return;
      }
      setSelectedAssetId(id);
    },
    [placementPhase, editMode, canBuildRoom, beginMoveAssetById]
  );

  const handleSelectPlayer = useCallback(
    (avatar: AvatarState) => {
      if (avatar.id === selfId) {
        setSelectedProfile(null);
        handleToggleSelfEmoteWheel();
        return;
      }
      setSelfEmoteWheelOpen(false);
      setSelectedAssetId(null);
      setSelectedProfile(avatar);
    },
    [selfId, handleToggleSelfEmoteWheel]
  );

  const handleCanvasReady = useCallback((canvas: HTMLCanvasElement) => {
    canvasRef.current = canvas;
  }, []);

  const handleChat = (text: string) => {
    sendSocket({ type: "chat", text });
  };

  const handleOpenSettings = () => {
    if (!isRoomOwner) return;
    setSettingsName(currentRoomName);
    setSettingsIsPrivate(currentRoomIsPrivate);
    setSettingsJoinMode(currentRoomJoinMode);
    setSettingsJoinPassword("");
    setSettingsTokenGateContract(currentRoomTokenGateContract);
    setSettingsAllowKnock(currentRoomAllowKnock);
    setSettingsLayoutId(activeLayout.id);
    setSettingsWallThemeId(activeWallThemeId);
    setSettingsFloorThemeId(activeFloorThemeId);
    setSettingsOpen(true);
  };

  const handleSaveRoomSettings = async () => {
    if (!isRoomOwner) {
      return;
    }
    const nextName = settingsName.trim().slice(0, 48);
    if (!nextName) {
      notifications.show({
        title: "Invalid room name",
        message: "Please enter a room name.",
        color: "red",
      });
      return;
    }
    const nextLayoutId = settingsLayoutId?.trim() || activeLayout.id;
    const nextWallThemeId = settingsWallThemeId?.trim() || activeWallThemeId;
    const nextFloorThemeId = settingsFloorThemeId?.trim() || activeFloorThemeId;
    const nextJoinMode = settingsJoinMode;
    const nextTokenGateContractRaw = settingsTokenGateContract.trim();
    const nextTokenGateTokenId = settingsTokenGateTokenId.trim();
    const nextTokenGateContract = nextTokenGateTokenId
      ? `${nextTokenGateContractRaw}:${nextTokenGateTokenId}`
      : nextTokenGateContractRaw;
    const nextIsPrivate = nextJoinMode !== "open";
    const updates: {
      name?: string;
      isPrivate?: boolean;
      joinMode?: RoomJoinMode;
      joinPassword?: string;
      tokenGateContract?: string;
      allowKnock?: boolean;
      layoutId?: string;
      wallThemeId?: string;
      floorThemeId?: string;
    } = {};
    if (nextName !== currentRoomName) {
      updates.name = nextName;
    }
    if (nextIsPrivate !== currentRoomIsPrivate) {
      updates.isPrivate = nextIsPrivate;
    }
    if (nextJoinMode !== currentRoomJoinMode) {
      updates.joinMode = nextJoinMode;
    }
    if (
      nextJoinMode === "password" &&
      currentRoomJoinMode !== "password" &&
      !settingsJoinPassword.trim()
    ) {
      notifications.show({
        title: "Password required",
        message: "Set a room password before enabling password gating.",
        color: "red",
      });
      return;
    }
    if (nextJoinMode === "password" && settingsJoinPassword.trim()) {
      updates.joinPassword = settingsJoinPassword.trim();
    }
    if (nextJoinMode === "token") {
      if (
        !nextTokenGateContractRaw ||
        !nextTokenGateContractRaw.startsWith("KT1")
      ) {
        notifications.show({
          title: "Token contract required",
          message: "Set a valid FA2 contract for token-gated rooms.",
          color: "red",
        });
        return;
      }
      if (nextTokenGateContract !== currentRoomTokenGateContract) {
        updates.tokenGateContract = nextTokenGateContract;
      }
    }
    if (settingsAllowKnock !== currentRoomAllowKnock) {
      updates.allowKnock = settingsAllowKnock;
    }
    if (nextLayoutId !== activeLayout.id) {
      updates.layoutId = nextLayoutId;
    }
    if (nextWallThemeId !== activeWallThemeId) {
      updates.wallThemeId = nextWallThemeId;
    }
    if (nextFloorThemeId !== activeFloorThemeId) {
      updates.floorThemeId = nextFloorThemeId;
    }
    if (
      updates.name === undefined &&
      updates.isPrivate === undefined &&
      updates.joinMode === undefined &&
      updates.joinPassword === undefined &&
      updates.tokenGateContract === undefined &&
      updates.allowKnock === undefined &&
      updates.layoutId === undefined &&
      updates.wallThemeId === undefined &&
      updates.floorThemeId === undefined
    ) {
      setSettingsOpen(false);
      return;
    }

    if (updates.layoutId !== undefined) {
      const confirmed = window.confirm(
        "Changing the room layout will reset ALL placed objects and paintings. Continue?"
      );
      if (!confirmed) {
        return;
      }
    }

    setSavingSettings(true);
    try {
      await updateRoom(session, roomId, updates);
      const visualSettingsChanged =
        updates.layoutId !== undefined ||
        updates.wallThemeId !== undefined ||
        updates.floorThemeId !== undefined;
      if (visualSettingsChanged) {
        setSceneReloadSeq((prev) => prev + 1);
      }
      if (updates.name !== undefined) {
        setCurrentRoomName(nextName);
      }
      if (updates.isPrivate !== undefined) {
        setCurrentRoomIsPrivate(nextIsPrivate);
      }
      if (updates.joinMode !== undefined) {
        setCurrentRoomJoinMode(nextJoinMode);
      }
      if (
        updates.joinMode !== undefined ||
        updates.tokenGateContract !== undefined
      ) {
        setCurrentRoomTokenGateContract(
          nextJoinMode === "token" ? nextTokenGateContract : ""
        );
      }
      if (updates.allowKnock !== undefined) {
        setCurrentRoomAllowKnock(settingsAllowKnock);
      }
      onRoomSettingsUpdated?.();
      setSettingsOpen(false);
      notifications.show({
        title: "Room updated",
        message: "Room settings were saved.",
        color: "teal",
      });
    } catch (error) {
      notifications.show({
        title: "Could not update room",
        message: error instanceof Error ? error.message : "Please retry.",
        color: "red",
      });
    } finally {
      setSavingSettings(false);
    }
  };

  const handleAskBot = (botId: string, prompt: string) => {
    sendSocket({ type: "ask_bot", botId, prompt });
  };

  const handleAdmitKnock = useCallback(
    async (knockId: string) => {
      setKnockActionId(knockId);
      try {
        await admitRoomKnock(session, roomId, knockId);
        setPendingKnocks((prev) =>
          prev.filter((knock) => knock.id !== knockId)
        );
        notifications.show({
          title: "Guest admitted",
          message: "The player can now enter this room.",
          color: "teal",
        });
      } catch (error) {
        notifications.show({
          title: "Could not admit knock",
          message: error instanceof Error ? error.message : "Please retry.",
          color: "red",
        });
      } finally {
        setKnockActionId((current) => (current === knockId ? null : current));
      }
    },
    [roomId, session]
  );

  const handleRejectKnock = useCallback(
    async (knockId: string) => {
      setKnockActionId(knockId);
      try {
        await rejectRoomKnock(session, roomId, knockId);
        setPendingKnocks((prev) =>
          prev.filter((knock) => knock.id !== knockId)
        );
      } catch (error) {
        notifications.show({
          title: "Could not reject knock",
          message: error instanceof Error ? error.message : "Please retry.",
          color: "red",
        });
      } finally {
        setKnockActionId((current) => (current === knockId ? null : current));
      }
    },
    [roomId, session]
  );

  const handleSnapshot = async () => {
    if (!canvasRef.current) return;
    const imageData = canvasRef.current.toDataURL("image/png", 0.92);
    await savePicture(session, roomId, imageData, {
      people: roomState?.players.map((p) => p.name) ?? [],
      room: roomId,
    });
  };

  const friendAddresses = useMemo(
    () =>
      new Set(
        friendLinks
          .filter((link) => link.status === "accepted")
          .map((link) =>
            link.userAddress === session.address
              ? link.friendAddress
              : link.userAddress
          )
      ),
    [friendLinks, session.address]
  );

  const [hoveredPlayerId, setHoveredPlayerId] = useState<string | null>(null);

  const selectedProfileFriendLink = selectedProfile
    ? friendLinks.find((link) => {
        const userAddress = link.userAddress.toLowerCase();
        const friendAddress = link.friendAddress.toLowerCase();
        const selfAddress = session.address.toLowerCase();
        const profileAddress = selectedProfile.address.toLowerCase();
        return (
          (userAddress === selfAddress && friendAddress === profileAddress) ||
          (friendAddress === selfAddress && userAddress === profileAddress)
        );
      }) ?? null
    : null;
  const selectedProfileFriendStatus = selectedProfileFriendLink?.status ?? null;
  const selectedProfileIncomingFriendRequest = Boolean(
    selectedProfileFriendLink &&
      selectedProfileFriendLink.status === "pending" &&
      selectedProfileFriendLink.userAddress.toLowerCase() ===
        selectedProfile?.address.toLowerCase() &&
      selectedProfileFriendLink.friendAddress.toLowerCase() ===
        session.address.toLowerCase()
  );

  const canSendFriendRequest = Boolean(
    selectedProfile &&
      selectedProfile.role === "player" &&
      selectedProfile.address.startsWith("tz") &&
      selectedProfile.address !== session.address &&
      selectedProfileFriendStatus == null &&
      onAddFriend
  );
  const canKickSelectedPlayer = Boolean(
    isRoomOwner &&
      selectedProfile &&
      selectedProfile.role === "player" &&
      selectedProfile.id !== selfId &&
      selectedProfile.address !== session.address
  );
  const canAcceptSelectedFriendRequest = Boolean(
    selectedProfile &&
      selectedProfile.role === "player" &&
      selectedProfileIncomingFriendRequest
  );

  const handleSendFriendRequest = async () => {
    if (!selectedProfile || !onAddFriend || !canSendFriendRequest) return;
    setSendingFriendRequest(true);
    try {
      await onAddFriend(selectedProfile.address);
      notifications.show({
        title: "Friend request sent",
        message: `Sent to ${selectedProfile.name}.`,
        color: "teal",
      });
    } catch (error) {
      notifications.show({
        title: "Could not send request",
        message:
          error instanceof Error ? error.message : "Please try again shortly.",
        color: "red",
      });
    } finally {
      setSendingFriendRequest(false);
    }
  };

  const handleAcceptSelectedFriendRequest = async () => {
    if (
      !selectedProfile ||
      !selectedProfileFriendLink ||
      !canAcceptSelectedFriendRequest
    ) {
      return;
    }
    setAcceptingFriendRequest(true);
    try {
      await onAcceptFriend(selectedProfileFriendLink);
      notifications.show({
        title: "Friend request accepted",
        message: `You and ${selectedProfile.name} are now friends.`,
        color: "teal",
      });
    } catch (error) {
      notifications.show({
        title: "Could not accept request",
        message:
          error instanceof Error ? error.message : "Please try again shortly.",
        color: "red",
      });
    } finally {
      setAcceptingFriendRequest(false);
    }
  };

  const handleReportPlayer = async () => {
    if (
      !selectedProfile ||
      !onReportUser ||
      !selectedProfile.address.startsWith("tz")
    ) {
      return;
    }
    setSendingReport(true);
    try {
      await onReportUser(selectedProfile.address, roomId);
      notifications.show({
        title: "Report sent",
        message: `${selectedProfile.name} has been reported for review.`,
        color: "orange",
      });
    } catch (error) {
      notifications.show({
        title: "Report failed",
        message:
          error instanceof Error ? error.message : "Please try again shortly.",
        color: "red",
      });
    } finally {
      setSendingReport(false);
    }
  };

  const handleKickSelectedPlayer = () => {
    if (!selectedProfile || !canKickSelectedPlayer) {
      return;
    }
    sendSocket({
      type: "kick_player",
      playerId: selectedProfile.id,
    });
    notifications.show({
      title: "Player removed",
      message: `${selectedProfile.name} was kicked from the room.`,
      color: "orange",
    });
    setSelectedProfile(null);
  };

  const handleBlockSelectedPlayer = async () => {
    if (!selectedProfile || !isRoomOwner) return;
    try {
      await blockUser(session, roomId, selectedProfile.address);
      // Also kick them from the room
      sendSocket({ type: "kick_player", playerId: selectedProfile.id });
      notifications.show({
        title: "User blocked",
        message: `${selectedProfile.name} has been blocked from this room.`,
        color: "red",
      });
    } catch {
      notifications.show({
        title: "Error",
        message: "Failed to block user.",
        color: "red",
      });
    }
    setSelectedProfile(null);
  };

  const handleRemoveSelectedFriend = async () => {
    if (!selectedProfile || !onRemoveFriend) return;
    const link = friendLinks.find(
      (f) =>
        (f.userAddress === selectedProfile.address ||
          f.friendAddress === selectedProfile.address) &&
        f.status === "accepted"
    );
    if (!link) return;
    try {
      await onRemoveFriend(link);
      notifications.show({
        title: "Friend removed",
        message: `${selectedProfile.name} has been removed from your friends.`,
        color: "gray",
      });
    } catch {
      notifications.show({
        title: "Error",
        message: "Failed to remove friend.",
        color: "red",
      });
    }
  };

  if (showRoomLoadingScreen) {
    return (
      <Box
        h="100vh"
        style={{
          display: "grid",
          placeItems: "center",
          background: "linear-gradient(180deg, #f0f2f5 0%, #fafbfc 100%)",
          padding: 20,
        }}
      >
        <Box
          w={420}
          p="xl"
          style={{
            ...glassPanel,
            borderRadius: 16,
          }}
        >
          <Stack align="center" gap="sm">
            <Loader size="lg" color="orange" />
            <Text fw={700} c="dark" fz={20}>
              Entering {displayRoomName}
            </Text>
            <Text size="sm" c="gray.6" ta="center">
              {roomLoadingMessage}
            </Text>
            <Button
              mt="sm"
              variant="subtle"
              leftSection={<IconArrowLeft size={16} />}
              onClick={onBack}
            >
              Back to lobby
            </Button>
          </Stack>
        </Box>
      </Box>
    );
  }

  return (
    <Box h="100vh" style={{ position: "relative", overflow: "hidden" }}>
      {/* Full-screen 3D Canvas */}
      <RoomCanvas
        key={`${roomId}:${sceneReloadSeq}:${activeLayout.id}:${activeWallThemeId}:${activeFloorThemeId}`}
        roomState={roomState}
        stateSampleTimeMs={stateSampleTimeMs}
        motionFeedRef={motionFeedRef}
        selfId={selfId}
        layout={activeLayout}
        theme={activeTheme}
        editMode={editMode}
        placementPhase={placementPhase}
        placingAsset={placingAsset}
        placementRotation={placementRotation}
        confirmedPos={confirmedPlacement}
        movingAssetId={movingAssetId}
        selectedAssetId={selectedAssetId}
        onSelectAsset={handleSelectAsset}
        onSelectPlayer={handleSelectPlayer}
        showSelfEmoteWheel={selfEmoteWheelOpen}
        onToggleSelfEmoteWheel={handleToggleSelfEmoteWheel}
        onDanceEmote={handleTriggerDanceEmote}
        onWaveEmote={handleTriggerWaveEmote}
        onMove={handleMove}
        onInteractAsset={handleInteractAsset}
        onLockPosition={handleLockPosition}
        onCanvasReady={handleCanvasReady}
      />

      <Modal
        opened={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        title="Room Settings"
        size="xl"
        centered
      >
        <Stack gap="sm">
          <TextInput
            label="Room Name"
            value={settingsName}
            onChange={(event) => setSettingsName(event.currentTarget.value)}
            maxLength={48}
          />
          <Switch
            label="Private Room"
            checked={settingsIsPrivate}
            onChange={(event) => {
              const checked = event.currentTarget.checked;
              setSettingsIsPrivate(checked);
              if (!checked && settingsJoinMode === "members") {
                setSettingsJoinMode("open");
              } else if (checked && settingsJoinMode === "open") {
                setSettingsJoinMode("members");
              }
            }}
          />
          <Select
            label="Join Access"
            value={settingsJoinMode}
            onChange={(value) => {
              const nextMode = (value as RoomJoinMode | null) ?? "open";
              setSettingsJoinMode(nextMode);
              if (nextMode !== "open") {
                setSettingsIsPrivate(true);
              } else {
                setSettingsIsPrivate(false);
              }
            }}
            data={[
              { value: "open", label: "Open (anyone can join)" },
              { value: "members", label: "Owner approval only" },
              { value: "password", label: "Password-gated" },
              { value: "token", label: "Token-gated (FA2)" },
            ]}
          />
          {settingsJoinMode === "password" && (
            <TextInput
              label="Room Password"
              placeholder="Set or rotate password"
              value={settingsJoinPassword}
              onChange={(event) =>
                setSettingsJoinPassword(event.currentTarget.value)
              }
            />
          )}
          {settingsJoinMode === "token" && (
            <>
              <TextInput
                label="FA2 Contract Address"
                placeholder="KT1..."
                value={settingsTokenGateContract}
                onChange={(event) =>
                  setSettingsTokenGateContract(event.currentTarget.value)
                }
                styles={{
                  input: { fontFamily: "monospace" },
                }}
              />
              <TextInput
                label="Token ID (optional)"
                description="Leave empty to allow any token from this contract"
                placeholder="e.g. 42"
                value={settingsTokenGateTokenId}
                onChange={(event) =>
                  setSettingsTokenGateTokenId(event.currentTarget.value)
                }
                styles={{
                  input: { fontFamily: "monospace" },
                }}
              />
            </>
          )}
          {settingsJoinMode !== "open" && (
            <Switch
              label="Allow Knock Requests"
              checked={settingsAllowKnock}
              onChange={(event) =>
                setSettingsAllowKnock(event.currentTarget.checked)
              }
            />
          )}
          {layoutChoices.length > 0 && (
            <Stack gap={6}>
              <Text size="sm" fw={600}>
                Layout
              </Text>
              <Box
                style={{
                  maxHeight: 420,
                  overflowY: "auto",
                  display: "grid",
                  gap: 10,
                  gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))",
                }}
              >
                {layoutChoices.map((layout) => (
                  <Card
                    key={layout.id}
                    p="xs"
                    radius="md"
                    style={{
                      cursor: "pointer",
                      border:
                        settingsLayoutId === layout.id
                          ? "1px solid rgba(45, 150, 255, 0.6)"
                          : "1px solid rgba(0,0,0,0.08)",
                      background:
                        settingsLayoutId === layout.id
                          ? "rgba(45, 150, 255, 0.06)"
                          : "#fafbfc",
                      minHeight: 210,
                    }}
                    onClick={() => setSettingsLayoutId(layout.id)}
                  >
                    <Stack gap={8}>
                      <LayoutPreviewCanvas
                        layout={layout}
                        theme={settingsPreviewTheme}
                        square
                        zoomMultiplier={0.96}
                      />
                      <Group
                        justify="space-between"
                        align="center"
                        wrap="nowrap"
                      >
                        <Text size="xs" fw={600} c="dark" lineClamp={1}>
                          {layout.name}
                        </Text>
                        <Badge size="xs" variant="light" color="teal">
                          {layout.maxUsers}
                        </Badge>
                      </Group>
                    </Stack>
                  </Card>
                ))}
              </Box>
              {settingsLayoutId !== activeLayout.id && (
                <Text size="xs" c="orange.7">
                  Warning: changing layout will reset ALL objects and paintings.
                </Text>
              )}
            </Stack>
          )}
          {themeChoices.length > 0 && (
            <Stack gap={6}>
              <Text size="sm" fw={600}>
                Wallpaper
              </Text>
              <Box
                style={{
                  maxHeight: 150,
                  overflowY: "auto",
                  display: "grid",
                  gap: 10,
                  gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))",
                }}
              >
                {themeChoices.map((theme) => (
                  <Card
                    key={`wall-${theme.id}`}
                    p="xs"
                    radius="md"
                    style={{
                      cursor: "pointer",
                      border:
                        settingsWallThemeId === theme.id
                          ? "1px solid rgba(45, 150, 255, 0.6)"
                          : "1px solid rgba(0,0,0,0.08)",
                      background:
                        settingsWallThemeId === theme.id
                          ? "rgba(45, 150, 255, 0.06)"
                          : "#fafbfc",
                    }}
                    onClick={() => setSettingsWallThemeId(theme.id)}
                  >
                    <Group justify="space-between" wrap="nowrap">
                      <Text size="xs" fw={600} c="dark" lineClamp={1}>
                        {theme.name}
                      </Text>
                      <Box
                        style={{
                          width: 18,
                          height: 18,
                          borderRadius: 4,
                          border: "1px solid rgba(0,0,0,0.15)",
                          background: theme.wall,
                          flexShrink: 0,
                        }}
                      />
                    </Group>
                  </Card>
                ))}
              </Box>
            </Stack>
          )}
          {themeChoices.length > 0 && (
            <Stack gap={6}>
              <Text size="sm" fw={600}>
                Floor
              </Text>
              <Box
                style={{
                  maxHeight: 155,
                  overflowY: "auto",
                  display: "grid",
                  gap: 10,
                  gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))",
                }}
              >
                {themeChoices.map((theme) => (
                  <Card
                    key={`floor-${theme.id}`}
                    p="xs"
                    radius="md"
                    style={{
                      cursor: "pointer",
                      border:
                        settingsFloorThemeId === theme.id
                          ? "1px solid rgba(45, 150, 255, 0.6)"
                          : "1px solid rgba(0,0,0,0.08)",
                      background:
                        settingsFloorThemeId === theme.id
                          ? "rgba(45, 150, 255, 0.06)"
                          : "#fafbfc",
                    }}
                    onClick={() => setSettingsFloorThemeId(theme.id)}
                  >
                    <Stack gap={6}>
                      <Text size="xs" fw={600} c="dark" lineClamp={1}>
                        {theme.name}
                      </Text>
                      <Group gap={6}>
                        <Box
                          style={{
                            width: 16,
                            height: 16,
                            borderRadius: 4,
                            border: "1px solid rgba(0,0,0,0.15)",
                            background: theme.floorTop,
                          }}
                        />
                        <Box
                          style={{
                            width: 16,
                            height: 16,
                            borderRadius: 4,
                            border: "1px solid rgba(0,0,0,0.15)",
                            background: theme.floorSide,
                          }}
                        />
                        <Box
                          style={{
                            width: 16,
                            height: 16,
                            borderRadius: 4,
                            border: "1px solid rgba(0,0,0,0.15)",
                            background: theme.porchMarker,
                          }}
                        />
                      </Group>
                    </Stack>
                  </Card>
                ))}
              </Box>
            </Stack>
          )}
          <Group justify="flex-end" mt="xs">
            <Button
              variant="default"
              onClick={() => setSettingsOpen(false)}
              disabled={savingSettings}
            >
              Cancel
            </Button>
            <Button
              loading={savingSettings}
              onClick={() => void handleSaveRoomSettings()}
            >
              Save
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Top-left info bar */}
      <Box
        style={{
          position: "absolute",
          top: 12,
          left: 12,
          zIndex: 50,
          pointerEvents: "auto",
        }}
      >
        <Paper
          p="xs"
          px="sm"
          radius="md"
          style={{
            ...glassPanel,
          }}
        >
          <Group gap="sm">
            <ActionIcon
              variant="subtle"
              size="sm"
              color="gray.7"
              onClick={onBack}
            >
              <IconArrowLeft size={16} />
            </ActionIcon>
            <Stack gap={0}>
              <Text fw={600} size="sm" c="dark" lh={1.2}>
                {displayRoomName}
              </Text>
              <Text size="xs" c="gray.6" lh={1.1}>
                Owner: {ownerAddressLabel}
              </Text>
            </Stack>
            <Badge
              variant="light"
              color={currentRoomIsPrivate ? "orange" : "teal"}
              size="sm"
            >
              {currentRoomIsPrivate ? "Private" : "Public"}
            </Badge>
            {isRoomOwner && (
              <Tooltip label="Room settings">
                <ActionIcon
                  variant="subtle"
                  size="sm"
                  color="gray.7"
                  onClick={handleOpenSettings}
                >
                  <IconSettings size={15} />
                </ActionIcon>
              </Tooltip>
            )}
            <Badge
              variant="dot"
              color={connected ? "teal" : "red"}
              size="sm"
              styles={{ root: { color: "white" } }}
            >
              {connected ? "Online" : "Offline"}
            </Badge>
            {waitlistPosition != null && (
              <Badge variant="light" color="yellow" size="sm">
                #{waitlistPosition}
              </Badge>
            )}
            <Text size="xs" c="gray.4">
              {roomState?.players.length ?? 0}/{roomState?.capacity ?? "?"}
            </Text>
          </Group>
        </Paper>
      </Box>

      {/* Bottom-left player profile card */}
      {selectedProfile && (
        <PlayerProfileCard
          profile={selectedProfile}
          friendStatus={selectedProfileFriendStatus}
          canAcceptFriendRequest={canAcceptSelectedFriendRequest}
          acceptingFriendRequest={acceptingFriendRequest}
          onAcceptFriendRequest={() => void handleAcceptSelectedFriendRequest()}
          canSendFriendRequest={canSendFriendRequest}
          sendingFriendRequest={sendingFriendRequest}
          onSendFriendRequest={() => void handleSendFriendRequest()}
          canReport={Boolean(
            onReportUser && selectedProfile.address.startsWith("tz")
          )}
          sendingReport={sendingReport}
          onReport={() => void handleReportPlayer()}
          canKick={canKickSelectedPlayer}
          onKick={handleKickSelectedPlayer}
          canBlock={
            isRoomOwner &&
            selectedProfile.address !== session.address &&
            selectedProfile.address.startsWith("tz")
          }
          onBlock={() => void handleBlockSelectedPlayer()}
          canRemoveFriend={
            selectedProfileFriendStatus === "accepted" &&
            Boolean(onRemoveFriend)
          }
          onRemoveFriend={() => void handleRemoveSelectedFriend()}
          onClose={() => setSelectedProfile(null)}
        />
      )}

      {/* Bottom action bar */}
      <Box
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          height: 64,
          zIndex: 100,
          pointerEvents: "auto",
          background: "#ffffff",
          borderTop: "1px solid rgba(0, 0, 0, 0.08)",
          boxShadow: "0 -2px 8px rgba(0, 0, 0, 0.06)",
        }}
      >
        <Group
          h="100%"
          px="md"
          justify="space-between"
          align="center"
          gap="md"
          style={{ maxWidth: 1400, margin: "0 auto" }}
        >
          {/* Left group - Chat & Players toggles */}
          <Group gap={8}>
            <Tooltip label="Chat (T)" position="top" withArrow>
              <ActionIcon
                size={40}
                radius="md"
                variant={chatOpen ? "filled" : "subtle"}
                color={chatOpen ? "orange" : "gray"}
                onClick={() => {
                  toggleChat();
                  closePlayers();
                }}
              >
                <IconMessageCircle size={20} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label="Players (P)" position="top" withArrow>
              <ActionIcon
                size={40}
                radius="md"
                variant={playersOpen ? "filled" : "subtle"}
                color={playersOpen ? "orange" : "gray"}
                onClick={() => {
                  togglePlayers();
                  closeChat();
                }}
              >
                <IconUsers size={20} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label="Friends" position="top" withArrow>
              <ActionIcon
                size={40}
                radius="md"
                variant={phoneOpen ? "filled" : "subtle"}
                color={phoneOpen ? "orange" : "gray"}
                onClick={togglePhone}
              >
                <Box style={{ position: "relative" }}>
                  <IconPhone size={20} />
                  {unreadDMCount > 0 && (
                    <Box
                      style={{
                        position: "absolute",
                        top: -6,
                        right: -8,
                        background: "#e8590c",
                        color: "#fff",
                        fontSize: 10,
                        fontWeight: 700,
                        borderRadius: 8,
                        minWidth: 16,
                        height: 16,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "0 4px",
                        lineHeight: 1,
                      }}
                    >
                      {unreadDMCount}
                    </Box>
                  )}
                </Box>
              </ActionIcon>
            </Tooltip>
            {((isRoomOwner && pendingKnocks.length > 0) ||
              pendingFriends.length > 0) &&
              (() => {
                const notifCount =
                  (isRoomOwner ? pendingKnocks.length : 0) +
                  pendingFriends.length;
                return (
                  <Tooltip
                    label={`Notifications (${notifCount})`}
                    position="top"
                    withArrow
                  >
                    <ActionIcon
                      size={40}
                      radius="md"
                      variant={knocksOpen ? "filled" : "light"}
                      color="orange"
                      onClick={() => toggleKnocks()}
                    >
                      <Box style={{ position: "relative" }}>
                        <IconBellRinging size={20} />
                        <Box
                          style={{
                            position: "absolute",
                            top: -6,
                            right: -8,
                            background: "#e8590c",
                            color: "#fff",
                            fontSize: 10,
                            fontWeight: 700,
                            borderRadius: 8,
                            minWidth: 16,
                            height: 16,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: "0 4px",
                            lineHeight: 1,
                          }}
                        >
                          {notifCount}
                        </Box>
                      </Box>
                    </ActionIcon>
                  </Tooltip>
                );
              })()}
          </Group>

          {/* Center - Persistent chat input */}
          <Box style={{ flex: 1, maxWidth: 560 }}>
            <TextInput
              ref={bottomBarInputRef}
              placeholder="Type a message..."
              value={bottomBarChatText}
              onChange={(e) => setBottomBarChatText(e.currentTarget.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && bottomBarChatText.trim()) {
                  e.preventDefault();
                  e.stopPropagation();
                  handleChat(bottomBarChatText.trim());
                  setBottomBarChatText("");
                } else if (e.key === "Escape") {
                  e.preventDefault();
                  (e.target as HTMLInputElement).blur();
                }
              }}
              size="md"
              radius="xl"
              rightSection={
                <ActionIcon
                  variant="filled"
                  color="orange"
                  size={30}
                  radius="xl"
                  disabled={!bottomBarChatText.trim()}
                  onClick={() => {
                    if (bottomBarChatText.trim()) {
                      handleChat(bottomBarChatText.trim());
                      setBottomBarChatText("");
                    }
                  }}
                >
                  <IconSend size={16} />
                </ActionIcon>
              }
              styles={{
                input: {
                  backgroundColor: "#f1f3f5",
                  border: "1px solid rgba(0, 0, 0, 0.08)",
                  paddingRight: 44,
                },
              }}
            />
          </Box>

          {/* Right group - Marketplace, Build, Snapshot */}
          <Group gap={8}>
            <Tooltip label="Marketplace" position="top" withArrow>
              <ActionIcon
                size={40}
                radius="md"
                variant="subtle"
                color="gray"
                onClick={() => onOpenMarketplace()}
              >
                <IconBuildingStore size={20} />
              </ActionIcon>
            </Tooltip>
            <Tooltip
              label={
                canBuildRoom
                  ? "Build (B)"
                  : "Build (Owner/Admin in public rooms)"
              }
              position="top"
              withArrow
            >
              <ActionIcon
                size={40}
                radius="md"
                variant={editMode ? "filled" : "subtle"}
                color={editMode ? "orange" : "gray"}
                disabled={!canBuildRoom}
                onClick={() => {
                  if (!canBuildRoom) return;
                  setEditMode((v) => !v);
                }}
              >
                <IconHammer size={20} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label="Snapshot (S)" position="top" withArrow>
              <ActionIcon
                size={40}
                radius="md"
                variant="subtle"
                color="gray"
                onClick={() => void handleSnapshot()}
              >
                <IconCamera size={20} />
              </ActionIcon>
            </Tooltip>
          </Group>
        </Group>
      </Box>

      {/* Chat popup */}
      {chatOpen && (
        <ChatPanel
          chatLog={chatLog}
          onAskBot={handleAskBot}
          onClose={closeChat}
        />
      )}

      {/* Players popup */}
      {playersOpen && (
        <PlayersPanel
          players={roomState?.players ?? []}
          bots={roomState?.bots ?? []}
          selfId={selfId}
          friendAddresses={friendAddresses}
          onSelectPlayer={handleSelectPlayer}
          onHoverPlayer={setHoveredPlayerId}
          onClose={closePlayers}
        />
      )}

      {/* Knocks popup */}
      {knocksOpen && (
        <KnocksPanel
          knocks={isRoomOwner ? pendingKnocks : []}
          actionId={knockActionId}
          onAdmit={(id) => void handleAdmitKnock(id)}
          onReject={(id) => void handleRejectKnock(id)}
          pendingFriends={pendingFriends}
          selfAddress={session.address}
          onAcceptFriend={(f) => void onAcceptFriend(f)}
          onClose={closeKnocks}
        />
      )}

      {/* Friends phone */}
      {phoneOpen && (
        <FriendsPhonePanel
          session={session}
          acceptedFriends={acceptedFriendLinks}
          pendingFriends={pendingFriends}
          friendsOnline={friendsOnline}
          unreadPerFriend={unreadPerFriend ?? {}}
          lastDmNotification={lastDmNotification ?? null}
          onAcceptFriend={(friend) => onAcceptFriend(friend)}
          onVisitRoom={(roomId) => onVisitFriendRoom?.(roomId)}
          onUnreadChange={() => {
            void refreshUnreadCount();
            onRefreshUnread?.();
          }}
          onClose={closePhone}
        />
      )}

      {/* Confirm HUD overlay */}
      {placementPhase === "confirming" && (
        <PlacementConfirmHUD
          onConfirm={handleConfirmPlacement}
          onRotate={rotatePlacementAsset}
          onDelete={handleDeleteMovingAsset}
          showDelete={Boolean(movingAssetId)}
          showRotate={placingAsset?.type !== "frame"}
          onCancel={() => {
            setPlacementPhase("placing");
            setConfirmedPlacement(null);
          }}
        />
      )}

      {editMode &&
        placingAsset &&
        (placementPhase === "placing" || placementPhase === "confirming") && (
          <Box
            style={{
              position: "absolute",
              top: 72,
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 205,
              pointerEvents: "none",
            }}
          >
            <Paper
              px="sm"
              py={6}
              radius="xl"
              style={{
                ...glassPanelDense,
                border: "1px solid rgba(255, 146, 43, 0.38)",
              }}
            >
              <Text size="xs" fw={700} c="orange.7" tt="uppercase" lts={0.8}>
                {placingAsset.type === "frame"
                  ? movingAssetId
                    ? "[Z] smaller   [X] bigger   [D] delete"
                    : "[Z] smaller   [X] bigger"
                  : movingAssetId
                  ? "[R] rotate   [D] delete"
                  : "[R] to rotate"}
              </Text>
            </Paper>
          </Box>
        )}

      {editMode &&
        placingAsset?.type === "glb" &&
        placingAsset.interactionType === "teleport" &&
        (placementPhase === "placing" || placementPhase === "confirming") && (
          <Box
            style={{
              position: "absolute",
              top: 112,
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 205,
              width: 360,
              pointerEvents: "auto",
            }}
          >
            <Paper
              p="sm"
              radius="md"
              style={{
                ...glassPanelDense,
                border: "1px solid rgba(34, 139, 230, 0.38)",
              }}
            >
              <Stack gap={6}>
                <Text size="xs" fw={700} c="blue.7" tt="uppercase" lts={0.8}>
                  Teleport Link
                </Text>
                <TextInput
                  size="xs"
                  value={placingAsset.teleportGroup ?? ""}
                  placeholder="e.g. lobby_portal_a"
                  onChange={(event) =>
                    setPlacingAsset((prev) =>
                      prev?.type === "glb"
                        ? {
                            ...prev,
                            teleportGroup: event.currentTarget.value,
                          }
                        : prev
                    )
                  }
                />
                <Text size="xs" c="dimmed">
                  Use the same link ID on another placed teleport to connect them.
                </Text>
              </Stack>
            </Paper>
          </Box>
        )}

      {/* Asset selection HUD overlay */}
      {selectedAssetId &&
        editMode &&
        placementPhase === "idle" &&
        (() => {
          const asset = roomState?.assets.find((a) => a.id === selectedAssetId);
          if (!asset) return null;
          return (
            <AssetSelectionHUD
              assetName={asset.name ?? "Object"}
              onMove={handleMoveSelected}
              onDelete={handleDeleteSelected}
              onDeselect={() => setSelectedAssetId(null)}
            />
          );
        })()}

      {/* Build Inventory Panel overlay — hidden while placing/confirming */}
      {editMode && placementPhase === "idle" && (
        <BuildInventoryPanel
          session={session}
          furnitureInventory={buildFurnitureInventory}
          onSelectFurniture={onSelectFurniture}
          sculptures={inventory.sculptures}
          paintings={inventory.paintings}
          loadingSculptures={inventory.loadingSculptures}
          loadingPaintings={inventory.loadingPaintings}
          sculpturePage={inventory.sculpturePage}
          paintingPage={inventory.paintingPage}
          sculptureHasMore={inventory.sculptureHasMore}
          paintingHasMore={inventory.paintingHasMore}
          placingAsset={placingAsset}
          onSelectAsset={handleSelectFromInventory}
          onExitBuild={() => setEditMode(false)}
          onPrevSculpturePage={() => {
            void inventory.prevSculpturesPage();
          }}
          onNextSculpturePage={() => {
            void inventory.nextSculpturesPage();
          }}
          onPrevPaintingPage={() => {
            void inventory.prevPaintingsPage();
          }}
          onNextPaintingPage={() => {
            void inventory.nextPaintingsPage();
          }}
        />
      )}

      {/* Idle warning overlay */}
      {idleWarningSeconds != null && (
        <Box
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 200,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(0,0,0,0.5)",
            pointerEvents: "auto",
          }}
        >
          <Paper
            p="xl"
            radius="md"
            shadow="xl"
            style={{
              maxWidth: 340,
              textAlign: "center",
              background: "#ffffff",
            }}
          >
            <IconAlertTriangle
              size={40}
              color="#ff922b"
              style={{ marginBottom: 12 }}
            />
            <Text fw={700} size="lg" c="dark" mb="xs">
              Are you still there?
            </Text>
            <Text size="sm" c="gray.6" mb="md">
              You'll be removed from the room in {idleWarningSeconds} seconds
              due to inactivity.
            </Text>
            <Button
              fullWidth
              color="orange"
              onClick={() => {
                // Send activity to reset server-side idle timer
                sendSocket({ type: "ping" });
                // We need a real activity message — ping doesn't count
                const self = roomState?.players.find((p) => p.id === selfId);
                if (self) {
                  sendSocket({
                    type: "move",
                    x: Math.round(self.x),
                    z: Math.round(self.z),
                  });
                }
                onDismissIdleWarning?.();
              }}
            >
              I'm here!
            </Button>
          </Paper>
        </Box>
      )}
    </Box>
  );
}
